
import React, { useState, useMemo } from 'react';
// FIX: Use namespace import for react-router-dom to fix potential module resolution errors.
import * as ReactRouterDom from 'react-router-dom';
import { COST_CATEGORIES, formatMonthForDisplay } from '../constants';
import { Project, ExpenseEntry, MainCostCategory, CostCategoryDetail, CostSubItem } from '../types';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { ArrowLeftIcon } from '../components/icons/ArrowLeftIcon';
import { DocumentPlusIcon } from '../components/icons/DocumentPlusIcon';
import { PencilSquareIcon } from '../components/icons/PencilSquareIcon';
import { TrashIcon } from '../components/icons/TrashIcon';
import { AddDirectExpenseModal } from '../components/AddDirectExpenseModal';
import { useExpenses } from '../hooks/useExpenses';
import { useProjects } from '../hooks/useProjects';
import { EyeIcon } from '../components/icons/EyeIcon';
import { DeleteConfirmationModal } from '../components/DeleteConfirmationModal';

const formatCurrency = (value?: number): string => {
  if (typeof value !== 'number' || isNaN(value)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

export const CostCategoryDetailPage: React.FC = () => {
  const { obraId, categoryId } = ReactRouterDom.useParams<{ obraId: string; categoryId: string }>();
  const navigate = ReactRouterDom.useNavigate();
  const { allExpenses, saveExpense, deleteExpense } = useExpenses();
  const { projects } = useProjects();

  const project = useMemo(() => projects.find(p => p.id === obraId), [projects, obraId]);
  
  const categoryDetails = useMemo(() => {
    return COST_CATEGORIES.find(c => c.id === categoryId) as CostCategoryDetail | undefined;
  }, [categoryId]);

  const categoryExpenses = useMemo(() => {
    const categoryName = categoryDetails?.name;
    return allExpenses.filter(e => 
        e.projectId === obraId && 
        (e.mainCategory?.toLowerCase() === categoryId?.toLowerCase() || e.mainCategory === categoryName) // Filter by ID or by full name to handle data inconsistency
    );
  }, [allExpenses, obraId, categoryId, categoryDetails]);

  const isDirectEntryCategory = useMemo(() => {
    return categoryId === MainCostCategory.DESPESAS_ADM ||
           categoryId === MainCostCategory.TRIBUTOS ||
           categoryId === MainCostCategory.TRIBUTOS_INSS;
  }, [categoryId]);
  
  const isLocked = project?.isLocked;

  // Modals State
  const [isDirectModalOpen, setIsDirectModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<ExpenseEntry | null>(null);
  const [expenseToDelete, setExpenseToDelete] = useState<ExpenseEntry | null>(null);

  const handleSubItemClick = (subItem: CostSubItem) => {
    navigate(`/obras/${project!.id}/custos/${categoryId}/${subItem.id}`);
  };

  const handleViewExpenseDetails = (expense: ExpenseEntry) => {
    if (!project || !categoryId) return;
    navigate(`/obras/${project.id}/custos/${categoryId}/${expense.subCategoryItemId}/${expense.id}`);
  };

  // --- Data Manipulation Handlers for Direct Entry Table ---
  const handleOpenAddModal = () => {
    setEditingExpense(null);
    setIsDirectModalOpen(true);
  }

  const handleOpenEditModal = (expense: ExpenseEntry) => {
    setEditingExpense(expense);
    setIsDirectModalOpen(true);
  }
  
  const handleCloseModal = () => {
    setIsDirectModalOpen(false);
    setEditingExpense(null);
  }

  const handleSaveExpense = (data: Omit<ExpenseEntry, 'id' | 'projectId'>) => {
    const payload: Partial<ExpenseEntry> = {
      ...editingExpense, // Spread existing expense if it's an edit
      ...data,           // Spread new data from the form
      projectId: obraId, // Add project ID
    };
    saveExpense(payload);
    handleCloseModal();
  }

  const handleOpenDeleteModal = (expense: ExpenseEntry) => {
    setExpenseToDelete(expense);
  };

  const handleConfirmDelete = () => {
    if (expenseToDelete) {
      deleteExpense(expenseToDelete.id);
      setExpenseToDelete(null);
    }
  };


  // --- Render ---
  if (!project || !categoryDetails) {
    return (
      <div className="text-center py-10">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-dark-text-primary">Dados da Obra ou Categoria não encontrados</h1>
        <Button onClick={() => navigate('/obras')} variant="secondary" className="mt-4">
          Voltar para Obras
        </Button>
      </div>
    );
  }

  const renderDirectEntryTable = () => (
    <Card title="Lançamentos" topRightAccessory={<Button onClick={handleOpenAddModal} leftIcon={<DocumentPlusIcon className="text-xl"/>} disabled={isLocked}>Adicionar Lançamento</Button>}>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-tertiary">
          <thead className="bg-gray-50 dark:bg-dark-secondary">
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">Mês</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">Descrição</th>
              <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">Valor</th>
              <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">Ações</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-dark-surface divide-y divide-gray-200 dark:divide-dark-tertiary">
            {categoryExpenses.length > 0 ? categoryExpenses.map(exp => (
              <tr key={exp.id} className="hover:bg-gray-50 dark:hover:bg-dark-secondary cursor-pointer" onClick={() => handleViewExpenseDetails(exp)}>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-dark-text-primary">{formatMonthForDisplay(exp.month)}</td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-dark-text-secondary">
                  <div>{exp.description}</div>
                  {exp.observation && <div className="text-xs italic text-gray-400 mt-1">Obs: {exp.observation}</div>}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-dark-text-primary text-right">{formatCurrency(exp.amount)}</td>
                <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-medium">
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleViewExpenseDetails(exp); }} className="p-1" title="Visualizar"><EyeIcon className="text-xl"/></Button>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleOpenEditModal(exp); }} className="p-1" title="Editar" disabled={isLocked}><PencilSquareIcon className="text-xl"/></Button>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleOpenDeleteModal(exp); }} className="p-1 text-red-500" title="Excluir" disabled={isLocked}><TrashIcon className="text-xl"/></Button>
                </td>
              </tr>
            )) : (
                <tr><td colSpan={4} className="text-center py-8 text-gray-500 dark:text-dark-text-secondary">Nenhum lançamento nesta categoria.</td></tr>
            )}
          </tbody>
           <tfoot className="bg-gray-50 dark:bg-dark-secondary">
              <tr>
                <td colSpan={2} className="px-4 py-3 text-right font-bold uppercase text-gray-600 dark:text-dark-text-secondary">Total</td>
                <td className="px-4 py-3 text-right font-bold text-brand-lime">{formatCurrency(categoryExpenses.reduce((sum, exp) => sum + exp.amount, 0))}</td>
                <td></td>
              </tr>
            </tfoot>
        </table>
      </div>
    </Card>
  );

  const renderSubItemsAsCards = () => {
    const expensesBySubItem = categoryExpenses.reduce((acc, exp) => {
        if (!acc[exp.subCategoryItemId]) {
          acc[exp.subCategoryItemId] = [];
        }
        acc[exp.subCategoryItemId].push(exp);
        return acc;
    }, {} as { [key: string]: ExpenseEntry[] });

    return (
         <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {categoryDetails.subItems.map(subItem => {
                const subItemExpenses = expensesBySubItem[subItem.id] || [];
                const subItemTotal = subItemExpenses.reduce((sum, exp) => sum + exp.amount, 0);

                return (
                    <Card
                      key={subItem.id}
                      className="bg-white dark:bg-dark-surface hover:shadow-xl hover:border-brand-teal border-2 border-transparent transition-all duration-200 cursor-pointer text-center flex flex-col justify-center min-h-[120px]"
                      onClick={() => handleSubItemClick(subItem)}
                    >
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary mb-1 leading-tight truncate" title={subItem.name}>
                        {subItem.name}
                      </h4>
                      <p className="text-xl font-bold text-brand-lime">
                        {formatCurrency(subItemTotal)}
                      </p>
                    </Card>
                )
            })}
         </div>
    );
  };
  
  const categoryTotal = categoryExpenses.reduce((sum, exp) => sum + exp.amount, 0);

  return (
    <div className="text-gray-900 dark:text-dark-text-primary">
      {/* Header Bar */}
      <div className="flex items-center mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/obras/${obraId}`)}
          className="p-2 mr-2 hover:bg-gray-100 dark:hover:bg-dark-tertiary"
          aria-label="Voltar para detalhes da obra"
        >
          <ArrowLeftIcon className="text-2xl" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold leading-tight">{project.name}</h1>
          <h2 className="text-lg font-normal text-brand-lime leading-tight">{categoryDetails.name}</h2>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="space-y-6">
        {/* Category Total */}
        {!isDirectEntryCategory && (
            <Card>
                <div className="flex flex-col md:flex-row justify-between md:items-center text-center md:text-left">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-dark-text-primary mb-2 md:mb-0">Custo Total da Categoria</h3>
                    <p className="text-3xl font-bold text-brand-lime">{formatCurrency(categoryTotal)}</p>
                </div>
            </Card>
        )}

        {/* Content */}
        {isDirectEntryCategory ? renderDirectEntryTable() : renderSubItemsAsCards()}
      </div>

      {/* --- Modals for Direct Entry --- */}
      {isDirectModalOpen && (
        <AddDirectExpenseModal
          isOpen={isDirectModalOpen}
          onClose={handleCloseModal}
          onSubmit={handleSaveExpense}
          categoryDetails={categoryDetails}
          existingExpense={editingExpense}
        />
      )}
      
      {expenseToDelete && (
        <DeleteConfirmationModal
          isOpen={!!expenseToDelete}
          onClose={() => setExpenseToDelete(null)}
          onConfirm={handleConfirmDelete}
          message={`Você tem certeza que deseja excluir o lançamento: "${expenseToDelete.description || 'Lançamento sem descrição'}"?`}
        />
      )}
    </div>
  );
};